import React, { useCallback, useEffect, useState } from 'react';

import { CustomSwitch } from '@/components/CustomSwitch';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { AcceptEdgeCombos, useAuthUserSettings, useUpdateUserSettings } from '@/hooks/use-auth-user-settings';
import { logger } from '@/utils/logging';

type EdgeComboSwitchProps = {
    displayMode: 'modal' | 'preferences';
};

const EDGE_COMBO_CHANGE_SWITCH = '[edge combo switch]: ';
export const EdgeComboSwitch = ({ displayMode }: EdgeComboSwitchProps) => {
    const [isEnabled, setIsEnabled] = useState(false);
    const { data } = useAuthUserSettings();
    const { mutateAsync: updateUserSettings } = useUpdateUserSettings();

    const updateUserAcceptComboSetting = useCallback(
        async (acceptCombo: AcceptEdgeCombos) => {
            await updateUserSettings({ accept_edge_combos: acceptCombo });
        },
        [updateUserSettings]
    );

    useEffect(() => {
        //If the edge combo setting is not defined for the user we show the dialog
        //with the toggle pre-set to "ENABLED"
        if (
            data?.accept_edge_combos === AcceptEdgeCombos.ENABLED ||
            data?.accept_edge_combos === AcceptEdgeCombos.UNDEFINED
        ) {
            setIsEnabled(true);
            updateUserAcceptComboSetting(AcceptEdgeCombos.ENABLED);
        }
    }, [data?.accept_edge_combos, updateUserAcceptComboSetting]);

    const handleProjectionChangeToggle = async () => {
        const newEnabledState = !isEnabled;
        if (displayMode === 'modal') {
            if (newEnabledState) {
                BetrAnalytics.trackEvent(AnalyticsEvent.ALWAYS_MAKE_EDGE_COMBO);
            } else {
                BetrAnalytics.trackEvent(AnalyticsEvent.ALWAYS_MAKE_EDGE_COMBO_OFF);
            }
        }
        setIsEnabled(newEnabledState);
        try {
            const acceptCombo = newEnabledState ? AcceptEdgeCombos.ENABLED : AcceptEdgeCombos.DISABLED;
            updateUserAcceptComboSetting(acceptCombo);
        } catch (error: unknown) {
            setIsEnabled(!newEnabledState);
            logger.error(EDGE_COMBO_CHANGE_SWITCH, 'Error updating user settings', error);
        }
    };

    return <CustomSwitch val={isEnabled} handleOnPress={handleProjectionChangeToggle} />;
};
